import { EyeEmpty, PinAlt } from 'iconoir-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LOADING } from '../../../redux_store/actionTypes';
import { getAllRides } from '../../../redux_store/rides/ride_thunk';
import { getUserStars } from '../../../utils';
import ExportDataButton from '../../templates/ExportDataButton';
import Pagination from '../../templates/Pagination';
import Tabs from '../../templates/Tabs';
import LoadingModal from '../../templates/modals/LoadingModal';

const TripsPage = () => {
  const dispatch = useDispatch();
  const navigator = useNavigate();
  const [tabSelected, setTabSelected] = useState('All');
  const { status, rides: trips } = useSelector((state) => state.ride);
  const rowsPerPage = 8;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(trips.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  useEffect(() => {
    dispatch(getAllRides(tabSelected.toLowerCase()));
    // eslint-disable-next-line
  }, []);

  const handleTabSelection = (e) => {
    const tab = e.target.innerText;
    dispatch(getAllRides(tab.toLowerCase()));
    setTabSelected(tab);
  }

  const getStatusBadge = (status) => {
    const styles = {
      'PENDING': 'badge-warning',
      'TO PICKUP': 'badge bg-slate-500 text-white',
      'AT PICKUP': 'badge bg-slate-700 text-white',
      'TO DESTINATION': 'badge-primary',
      'AT DESTINATION': 'badge bg-cyan-600 text-white',
      'COMPLETED': 'badge-success',
      'RATED': 'badge bg-orange-500 text-white',
      'CANCELLED': 'badge-danger',
    };

    const labels = {
      'PENDING': 'Pending',
      'TO PICKUP': 'To Pickup',
      'AT PICKUP': 'At Pickup',
      'TO DESTINATION': 'To Destination',
      'AT DESTINATION': 'At Destination',
      'COMPLETED': 'Completed',
      'RATED': 'Rated',
      'CANCELLED': 'Cancelled',
    };

    return (
      <span className={styles[status] || 'badge bg-slate-400 text-white'}>
        {labels[status] || 'Unknown'}
      </span>
    );
  };

  return (
    <div className='space-y-6'>
      {status === LOADING && <LoadingModal message={'Loading data...'} />}

      {/* Page Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-surface-800'>Trip Management</h1>
          <p className='text-surface-500 mt-1'>Monitor and manage all ride requests</p>
        </div>
        <ExportDataButton data={trips} filename='wayride-trips' />
      </div>

      {/* Filters */}
      <div className='card'>
        <Tabs
          active={tabSelected}
          heading={['All', 'On-Going', 'Completed', 'Cancelled', 'Rated']}
          onTabSelect={handleTabSelection}
        />
      </div>

      {/* Trips Table */}
      <div className='card p-0 overflow-hidden'>
        <table className="table-modern">
          <thead>
            <tr>
              <th>Driver</th>
              <th>Customer</th>
              <th>From</th>
              <th>To</th>
              <th>Type</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {trips && trips.length > 0 ?
              trips.slice(startIndex, endIndex).map((trip, index) => (
                <tr key={trip.id || index}>
                  {trip.driver_id ? (
                    <td>
                      <div className="flex items-center gap-3">
                        <img
                          className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                          src={trip.driver.profile_pic}
                          alt=""
                          loading="lazy"
                        />
                        <div>
                          <p className="font-semibold text-surface-800">
                            {trip.driver.surname} {trip.driver.other_names}
                          </p>
                          <p className="text-xs text-surface-400 flex items-center gap-1">
                            {getUserStars(trip.driver.ratings)}
                          </p>
                        </div>
                      </div>
                    </td>
                  ) : (
                    <td>
                      <span className='text-surface-400 italic'>Not assigned</span>
                    </td>
                  )}
                  <td>
                    <div className="flex items-center gap-3">
                      <img
                        className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                        src={trip.customer.profile_pic}
                        alt=""
                        loading="lazy"
                      />
                      <div>
                        <p className="font-semibold text-surface-800">
                          {trip.customer.surname} {trip.customer.other_names}
                        </p>
                        <p className="text-xs text-surface-400 flex items-center gap-1">
                          {getUserStars(trip.customer.ratings)}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <p className='max-w-[150px] truncate text-surface-600' title={trip.origin_description}>
                      {trip.origin_description}
                    </p>
                  </td>
                  <td>
                    <p className='max-w-[150px] truncate text-surface-600' title={trip.destination_description}>
                      {trip.destination_description}
                    </p>
                  </td>
                  <td>
                    <span className='badge bg-surface-100 text-surface-600'>
                      {trip.is_delivery ? 'Delivery' : 'Ride'}
                    </span>
                  </td>
                  <td>
                    {getStatusBadge(trip.status)}
                  </td>
                  <td>
                    <button
                      onClick={() => navigator(`/trips/${trip.id}`)}
                      className='btn-ghost p-2 rounded-lg'
                      title='View Details'
                    >
                      <EyeEmpty strokeWidth={2} className='w-4 h-4' />
                    </button>
                  </td>
                </tr>
              )) :
              <tr>
                <td colSpan={7} className="text-center py-12">
                  <div className='flex flex-col items-center gap-3'>
                    <div className='w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center'>
                      <PinAlt strokeWidth={1.5} className='w-8 h-8 text-surface-400' />
                    </div>
                    <div>
                      <p className='font-medium text-surface-600'>No trips found</p>
                      <p className='text-sm text-surface-400'>Try adjusting your filters</p>
                    </div>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='border-t border-surface-100 px-6 py-4'>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default TripsPage
